/**
 * Admin — Kit Reimbursement Payout Runs (Feb 2026).
 *
 * Bundles every `eligible` partner-kit-reimbursement ledger row into
 * per-partner payout runs so accounting can settle multiple
 * reimbursements in a single Xero bill. See
 * `kit_reimb_payout_run_service.py` for the state-machine spec.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, RefreshCw, Loader2, CheckCircle2, Ban, Download,
  Package, ArrowLeft, AlertTriangle, Play, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const CENTS = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  awaiting_payment: { label: "Awaiting Payment", tone: "bg-amber-100 text-amber-800 border-amber-200" },
  paid:             { label: "Paid",             tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled:        { label: "Cancelled",        tone: "bg-slate-100 text-slate-600 border-slate-200" },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { label: status, tone: "bg-slate-100 text-slate-500 border-slate-200" };
  return (
    <span
      data-testid={`kit-reimb-run-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${s.tone}`}
    >
      {s.label}
    </span>
  );
}

export default function AdminKitReimbursementPayoutRuns() {
  const [eligibility, setEligibility] = useState(null);
  const [runs, setRuns] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [paidTarget, setPaidTarget] = useState(null);
  const [paidBillRef, setPaidBillRef] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [eligResp, runsResp] = await Promise.all([
        api.get("/admin/kit-reimbursement-payout-runs/eligible-summary"),
        api.get("/admin/kit-reimbursement-payout-runs"),
      ]);
      setEligibility(eligResp.data);
      setRuns(runsResp.data.runs || []);
      setTotals(runsResp.data.totals_by_status || {});
    } catch {
      toast.error("Failed to load payout runs.");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const grandTotal = useMemo(
    () => Object.values(totals).reduce((acc, t) => acc + (t.cents || 0), 0),
    [totals],
  );

  const createOne = async (partnerId, partnerName) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/kit-reimbursement-payout-runs", {
        partner_id: partnerId,
      });
      toast.success(
        `Created ${data.run.kit_reimb_payout_reference} for ${partnerName} · ${CENTS(data.run.total_cents)}`,
      );
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Create failed."));
    } finally { setBusy(false); }
  };

  const bulkCreate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/kit-reimbursement-payout-runs/bulk-create");
      if (data.runs_created === 0) {
        toast.info("No eligible rows found — nothing to bundle.");
      } else {
        toast.success(
          `Bundled ${data.total_rows} rows into ${data.runs_created} run${data.runs_created === 1 ? "" : "s"} · ${CENTS(data.total_cents)}`,
        );
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk create failed.");
    } finally { setBusy(false); }
  };

  const markPaid = async () => {
    if (!paidTarget) return;
    setBusy(true);
    try {
      await api.post(
        `/admin/kit-reimbursement-payout-runs/${paidTarget.id}/mark-paid`,
        { xero_bill_ref: paidBillRef.trim() || null },
      );
      toast.success(`Marked ${paidTarget.kit_reimb_payout_reference} paid.`);
      setPaidTarget(null); setPaidBillRef("");
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.code || d?.message || "Mark-paid failed.");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!cancelTarget) return;
    if ((cancelReason || "").trim().length < 3) {
      toast.error("Add a reason (min 3 chars)."); return;
    }
    setBusy(true);
    try {
      await api.post(
        `/admin/kit-reimbursement-payout-runs/${cancelTarget.id}/cancel`,
        { reason: cancelReason.trim() },
      );
      toast.success(`Cancelled ${cancelTarget.kit_reimb_payout_reference}. Ledger rows released.`);
      setCancelTarget(null); setCancelReason("");
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.code || d?.message || "Cancel failed.");
    } finally { setBusy(false); }
  };

  const downloadXero = (run) => {
    window.open(`${API_BASE}/api/admin/kit-reimbursement-payout-runs/${run.id}/xero-export.csv`, "_blank");
  };

  return (
    <PortalShell>
      <div className="p-6 space-y-5" data-testid="admin-kit-reimb-runs-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/admin/kit-reimbursements" className="text-xs text-cyan-700 hover:underline flex items-center gap-1 mb-1">
              <ArrowLeft className="w-3 h-3" /> Back to Kit Reimbursements
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-6 h-6" /> Kit Reimbursement Payout Runs
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Bundles every eligible kit reimbursement into a per-partner
              payout run for Xero export. Mark paid once accounting has
              settled the bill; cancel to release rows back to the queue.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={reload} variant="outline" size="sm"
                    data-testid="admin-kit-reimb-runs-refresh"
                    className="gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Eligibility summary + Bundle CTA */}
        <div
          data-testid="admin-kit-reimb-runs-eligibility"
          className="rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-5"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-800">Ready to bundle</div>
              <div className="text-2xl font-bold text-slate-900 mt-0.5">
                {eligibility?.total_partners || 0}{" "}
                <span className="text-sm font-normal text-slate-600">
                  partner{(eligibility?.total_partners || 0) === 1 ? "" : "s"} ·
                  {" "}{eligibility?.total_rows || 0} row{(eligibility?.total_rows || 0) === 1 ? "" : "s"} ·
                  {" "}<span className="font-mono font-bold text-cyan-800">{CENTS(eligibility?.total_cents)}</span>
                </span>
              </div>
            </div>
            <Button
              type="button"
              disabled={busy || !(eligibility?.total_rows > 0)}
              onClick={bulkCreate}
              data-testid="admin-kit-reimb-runs-bulk-create"
              className="gap-1.5 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50"
            >
              <Play className="w-4 h-4" /> Bundle all into runs
            </Button>
          </div>
          {(eligibility?.partners || []).length > 0 && (
            <div className="mt-4 divide-y divide-cyan-100 rounded-lg border border-cyan-100 bg-white">
              {eligibility.partners.map((p) => (
                <div
                  key={p.partner_id}
                  data-testid={`admin-kit-reimb-runs-elig-${p.partner_id}`}
                  className="px-3 py-2 flex flex-wrap items-center gap-3"
                >
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-sm font-semibold text-slate-900">{p.partner_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{p.partner_slug || "—"}</div>
                  </div>
                  <div className="text-xs text-slate-600 tabular-nums">
                    {p.eligible_count} row{p.eligible_count === 1 ? "" : "s"}
                  </div>
                  <div className="font-mono font-bold tabular-nums text-slate-900">
                    {CENTS(p.amount_cents)}
                  </div>
                  {p.bank_details_status !== "complete" && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                      title="Bank details not fully supplied for this partner"
                    >
                      <AlertTriangle className="w-3 h-3" /> Bank {p.bank_details_status}
                    </span>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => createOne(p.partner_id, p.partner_name)}
                    data-testid={`admin-kit-reimb-runs-create-${p.partner_id}`}
                    className="text-[11px] h-7"
                  >
                    Create run
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Roll-up tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["awaiting_payment", "paid", "cancelled"].map((s) => {
            const t = totals[s] || { count: 0, cents: 0 };
            const cfg = STATUS_BADGE[s];
            return (
              <div key={s} data-testid={`admin-kit-reimb-runs-total-${s}`} className={`p-3 rounded-xl border ${cfg.tone}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{cfg.label}</div>
                <div className="text-xl font-bold mt-0.5 tabular-nums">
                  {t.count} <span className="text-xs font-normal opacity-70">· {CENTS(t.cents)}</span>
                </div>
              </div>
            );
          })}
          <div className="p-3 rounded-xl border border-slate-200 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Grand Total</div>
            <div className="text-xl font-bold mt-0.5 text-slate-900 tabular-nums">{CENTS(grandTotal)}</div>
          </div>
        </div>

        {/* Runs table */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-kit-reimb-runs-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Partner</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Rows</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Xero bill</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={8} className="py-8 text-center text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && runs.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-500">
                    No payout runs yet. Bundle some from the eligibility panel above.
                  </td></tr>
                )}
                {!loading && runs.map((run) => (
                  <tr key={run.id} data-testid={`admin-kit-reimb-run-row-${run.id}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold">
                      {run.kit_reimb_payout_reference}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs font-semibold text-slate-900">{run.partner_name || "—"}</div>
                      <div className="text-[10px] font-mono text-slate-500">{run.partner_slug || ""}</div>
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={run.status} /></td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{run.row_count}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-slate-900">
                      {CENTS(run.total_cents)}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {run.created_at ? new Date(run.created_at).toLocaleDateString("en-AU") : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-600">
                      {run.xero_bill_ref || <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => downloadXero(run)}
                          data-testid={`admin-kit-reimb-run-xero-${run.id}`}
                          className="text-[11px] h-7 gap-1"
                        >
                          <Download className="w-3 h-3" /> Xero CSV
                        </Button>
                        {run.status === "awaiting_payment" && (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              disabled={busy}
                              onClick={() => { setPaidTarget(run); setPaidBillRef(""); }}
                              data-testid={`admin-kit-reimb-run-mark-paid-${run.id}`}
                              className="text-[11px] h-7 bg-emerald-600 hover:bg-emerald-700 gap-1"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Mark paid
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={busy}
                              onClick={() => { setCancelTarget(run); setCancelReason(""); }}
                              data-testid={`admin-kit-reimb-run-cancel-${run.id}`}
                              className="text-[11px] h-7 border-rose-300 text-rose-700 hover:bg-rose-50 gap-1"
                            >
                              <Ban className="w-3 h-3" /> Cancel
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mark-paid modal */}
      {paidTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => { if (!busy) { setPaidTarget(null); setPaidBillRef(""); } }}
          data-testid="admin-kit-reimb-run-mark-paid-modal"
        >
          <div className="bg-white rounded-2xl p-5 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <h3 className="text-lg font-bold text-slate-900">Mark payout run as paid</h3>
            </div>
            <div className="text-xs text-slate-600 mb-3 space-y-0.5">
              <div><strong>Reference:</strong> {paidTarget.kit_reimb_payout_reference}</div>
              <div><strong>Partner:</strong> {paidTarget.partner_name}</div>
              <div><strong>Amount:</strong> {CENTS(paidTarget.total_cents)} · {paidTarget.row_count} rows</div>
            </div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Xero bill ref (optional)</label>
            <input
              value={paidBillRef}
              onChange={(e) => setPaidBillRef(e.target.value)}
              placeholder="BILL-0042"
              data-testid="admin-kit-reimb-run-mark-paid-billref"
              className="w-full text-sm p-2 border border-slate-200 rounded"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Paste the Xero bill number once you&apos;ve entered the CSV.
              This is a one-way action — ledger rows flip to paid.
            </div>
            <div className="flex gap-2 justify-end mt-3">
              <Button type="button" variant="outline" size="sm" disabled={busy}
                      onClick={() => { setPaidTarget(null); setPaidBillRef(""); }}
                      data-testid="admin-kit-reimb-run-mark-paid-cancel">Cancel</Button>
              <Button type="button" size="sm" disabled={busy} onClick={markPaid}
                      data-testid="admin-kit-reimb-run-mark-paid-submit"
                      className="bg-emerald-600 hover:bg-emerald-700 gap-1">
                <CheckCircle2 className="w-3 h-3" /> Confirm paid
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {cancelTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => { if (!busy) { setCancelTarget(null); setCancelReason(""); } }}
          data-testid="admin-kit-reimb-run-cancel-modal"
        >
          <div className="bg-white rounded-2xl p-5 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              <h3 className="text-lg font-bold text-slate-900">Cancel payout run</h3>
            </div>
            <div className="text-xs text-slate-600 mb-3 space-y-0.5">
              <div><strong>Reference:</strong> {cancelTarget.kit_reimb_payout_reference}</div>
              <div><strong>Partner:</strong> {cancelTarget.partner_name}</div>
              <div><strong>Amount:</strong> {CENTS(cancelTarget.total_cents)} · {cancelTarget.row_count} rows</div>
              <div className="pt-1 text-rose-700 font-semibold">
                Ledger rows will be released back to the &ldquo;Eligible&rdquo; queue.
              </div>
            </div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Reason (min 3 chars)</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="e.g. wrong partner selected; rebundling"
              data-testid="admin-kit-reimb-run-cancel-reason"
              className="w-full text-sm p-2 border border-slate-200 rounded"
            />
            <div className="flex gap-2 justify-end mt-3">
              <Button type="button" variant="outline" size="sm" disabled={busy}
                      onClick={() => { setCancelTarget(null); setCancelReason(""); }}
                      data-testid="admin-kit-reimb-run-cancel-cancel">
                <X className="w-3 h-3 mr-1" /> Keep run
              </Button>
              <Button type="button" size="sm" disabled={busy} onClick={cancel}
                      data-testid="admin-kit-reimb-run-cancel-submit"
                      className="bg-rose-600 hover:bg-rose-700 gap-1">
                <Ban className="w-3 h-3" /> Cancel run
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
